import {
  CheckCircleOutlined,
  EditOutlined,
  ExperimentOutlined,
  EyeOutlined,
  MinusCircleOutlined,
  ToolOutlined,
} from '@ant-design/icons'
import { Stack } from '@sureco/design-system'
import {
  Button,
  Tag,
  Tooltip,
  Typography,
  notification,
} from '@surecompanies/core_components'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { CompanyStatusEnum } from 'constants/company'
import client from 'enrollment-platform/api/eps.client'
import { isDemoCustomer } from 'enrollment-platform/pages/DemoCompanyManagement/DemoCustomer/utils'
import React, { useEffect, useState } from 'react'

/**
 * Mirrors the backend TEST_COMPANY_NAME_REGEX used in reconciliation.
 * Matches names containing "test", "dummy", "sandbox" as substrings,
 * or whole-word "poc", "demo", "qa", "donotuse".
 * Swap for an `isTestClient` API field once it surfaces on the list endpoint.
 */
const TEST_COMPANY_NAME_REGEX =
  /(test|dummy|sandbox)|(^|[^a-z])(poc|demo|qa|donotuse|do not use)([^a-z]|$)/i

const isTestCompany = (record: { name: string }): boolean =>
  TEST_COMPANY_NAME_REGEX.test(record.name)

export const IconStatusMapper = {
  active: {
    color: '#98CE24',
    text: '#fff',
    icon: <CheckCircleOutlined />,
  },
  draft: {
    color: '#FAAD14',
    text: '#fff',
    icon: <EditOutlined />,
  },
  inactive: { color: '#999', text: '#fff', icon: <MinusCircleOutlined /> },
}

type UseListCustomerProps = {
  onView: (e: any) => void
  onEdit: (e: any) => void
  page?: number
  limit?: number
}

type CustomerListResult = {
  id: string
  employee_count: number
  status: string
  name: string
}

type CustomerListResultDTO = {
  data: CustomerListResult[]
  meta: {
    total: number
    page: number
    limit: number
  }
}

export type SortOrder = 'asc' | 'desc'
export type SortBy = 'name' | 'employee_count' | 'status'
export type Filter = {
  name?: string
  status?: string[]
  sortBy?: SortBy
  sortOrder?: SortOrder
}
export type Sorter = {
  field: SortBy
  order: 'ascend' | 'descend'
}

export default function useListCustomer({
  onView,
  onEdit,
  page = 1,
  limit = 200,
}: UseListCustomerProps) {
  const [filter, setFilter] = useState<Filter>({
    name: undefined,
    status: undefined,
    sortBy: undefined,
    sortOrder: undefined,
  })
  const [apiParams, setApiParams] = useState<Filter>({
    name: undefined,
    status: undefined,
    sortBy: undefined,
    sortOrder: undefined,
  })
  const [cancelToken, setCancelToken] = useState<any>(null)

  async function listCustomer(): Promise<CustomerListResultDTO> {
    try {
      if (cancelToken) {
        cancelToken.cancel('Request cancelled due to new search')
      }

      const source = axios.CancelToken.source()
      setCancelToken(source)

      const result = await client.get<CustomerListResultDTO>('/customers', {
        params: {
          page,
          limit,
          name: apiParams.name,
          status: apiParams.status,
          sortBy: apiParams.sortBy,
          sortOrder: apiParams.sortOrder,
        },
        cancelToken: source.token,
      })

      return result.data
    } catch (err) {
      if (axios.isCancel && axios.isCancel(err)) {
        throw err
      }

      notification.error({
        message: 'Something went wrong displaying customers',
      })

      return {
        data: [],
        meta: {
          total: 0,
          page,
          limit,
        },
      }
    }
  }

  const { isPending, error, data, refetch } = useQuery({
    queryKey: ['getCustomers', page, limit, apiParams],
    queryFn: listCustomer,
    staleTime: 0,
    enabled: true,
    retry: (failureCount, error) => {
      if (axios.isCancel && axios.isCancel(error)) {
        return false
      }

      return failureCount < 3
    },
  })

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      filters: [
        {
          text: 'Draft',
          value: CompanyStatusEnum.DRAFT,
        },
        {
          text: 'Active',
          value: CompanyStatusEnum.ACTIVE,
        },
        {
          text: 'Inactive',
          value: CompanyStatusEnum.INACTIVE,
        },
      ],
      sorter: () => {},
      render: (_, { status }) => {
        const config = IconStatusMapper[status.toLowerCase()]
        return (
          <>
            <Tag
              icon={config.icon}
              color={config.color}
              style={{ borderRadius: 18 }}
            >
              <Typography.Text
                type={config.color}
                style={{
                  color: config.text,
                  fontSize: 14,
                  fontWeight: 'bolder',
                }}
              >
                {status}
              </Typography.Text>
            </Tag>
          </>
        )
      },
    },
    {
      title: 'Company Name',
      dataIndex: 'name',
      sorter: () => {},
      render: (name: string, record) => {
        if (isDemoCustomer(record)) {
          return (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: '#fff7e6',
                border: '1px solid #ffd591',
                borderRadius: 4,
                padding: '2px 8px',
                color: '#873800',
              }}
            >
              <ExperimentOutlined style={{ color: '#fa8c16', flexShrink: 0 }} />
              {name}
            </span>
          )
        }

        if (isTestCompany(record)) {
          return (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: '#F4F7EC',
                border: '1px solid #C5D69B',
                borderRadius: 4,
                padding: '2px 8px',
                color: '#5F7630',
              }}
            >
              <ToolOutlined style={{ color: '#81A03D', flexShrink: 0 }} />
              {name}
            </span>
          )
        }

        return name
      },
    },
    {
      title: 'Employees',
      dataIndex: 'employee_count',
      sorter: () => {},
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Stack direction="row" space={10}>
          <Tooltip title="View">
            <Button
              type="primary"
              shape="circle"
              icon={<EyeOutlined />}
              onClick={() => onView(record)}
            />
          </Tooltip>
          {!isDemoCustomer(record) && (
            <Tooltip title="Edit">
              <Button
                shape="circle"
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
              />
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ]

  useEffect(() => {
    const newApiParams = {
      ...filter,
      name: filter.name?.trim() || undefined,
    }
    setApiParams(newApiParams)
  }, [filter])

  useEffect(() => {
    refetch()
  }, [page, limit, apiParams, refetch])

  useEffect(() => {
    return () => {
      if (cancelToken) {
        cancelToken.cancel('Component unmounted')
      }
    }
  }, [cancelToken])
  return {
    dataSource: data?.data,
    total: data?.meta?.total,
    isLoading: isPending,
    error,
    columns,
    refetch,
    filter,
    setFilter,
  }
}
